/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Action,
  ActionIcon, Card, ConfirmModal, FormHandlers, Grid, HStack, ICON_CHECK, ICON_CLOSE, ICON_PENCIL_OUTLINE, ICON_TRASH_CAN_OUTLINE, Message, Portal, Stack,
  useModal,
  useNotifications,
  SimpleTable,
  Text,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import hash from 'object-hash';
import { Commission, Invoice } from '../../../providers/api/dtos';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import InvoiceCommissionForm, { InvoiceCommissionFormProps } from '../../components/invoice-commission-form/InvoiceCommissionForm';
import * as styles from './ManageInvoiceCommissions.style';
import { checkCommissionLimits } from './utils';
import Divider from '../../../components/divider/Divider';
import { updateInvoice } from '../../../providers/api/invoice/invoiceProvider';
import InvoiceCommissionTableRow from '../../components/invoice-commission-table-row/InvoiceCommissionTableRow';
import InvoiceCommissionCard from '../../components/invoice-commission-card/InvoiceCommissionCard';
import useCommissionSuggestions from '../../hooks/use-commission-suggestions/useCommissionSuggestions';
import { commissionFormModelToCommission, commissionToCommissionFormModel } from '../../components/invoice-commission-form/mappers';

type FormRef = React.MutableRefObject<FormHandlers>;

export type ManageInvoiceCommissionsProps = {
  invoice: Invoice,
  onUpdate?: (invoice: Invoice) => void,
};

export const CREATE_COMMISSION_SUCCESS_MESSAGE = 'Commissione aggiunta con successo.';
export const CREATE_COMMISSION_ERROR_MESSAGE = 'Non è stato possibile aggiungere la commissione.';
export const UPDATE_COMMISSION_SUCCESS_MESSAGE = 'Commissione aggiornata con successo.';
export const UPDATE_COMMISSION_ERROR_MESSAGE = 'Non è stato possibile aggiornare la commissione.';
export const DELETE_COMMISSION_SUCCESS_MESSAGE = 'Commissione eliminata con successo.';
export const DELETE_COMMISSION_ERROR_MESSAGE = 'Non è stato possibile eliminare la commissione.';

const ManageInvoiceCommissions: React.FC<ManageInvoiceCommissionsProps> = ({
  invoice,
  onUpdate,
}) => {
  const isMobile = useIsDevice('mobile');
  const [isSaving, setIsSaving] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState<Commission | false>(false);

  const { data: suggestedCommissions } = useCommissionSuggestions(invoice);

  const updateCommissionFormRefs = React.useRef<{ [key: string]: FormRef }>({});
  const createCommissionFormRef = React.useRef<FormHandlers>(null) as FormRef;

  const { addSuccess, addError } = useNotifications();
  const confirmDeleteModal = useModal<Commission | undefined>();

  const addCommission = React.useCallback(async (newCommission: Commission) => {
    const isValid = checkCommissionLimits(
      newCommission,
      invoice,
      addError,
    );

    if (!isValid) return false;

    setIsSaving(true);

    try {
      const commissions = [
        ...invoice.commissions,
        newCommission,
      ];

      const savedInvoice = await updateInvoice(invoice.id!, {
        ...invoice,
        commissions,
      });

      setIsSaving(false);
      addSuccess(CREATE_COMMISSION_SUCCESS_MESSAGE);
      onUpdate?.(savedInvoice);
    } catch (error) {
      setIsSaving(false);
      addError(CREATE_COMMISSION_ERROR_MESSAGE);
    }

    return true;
  }, [invoice, addError, addSuccess, onUpdate]);

  const getUpdateCommissionFormRef = React.useCallback((hashedCommission: string): FormRef => {
    if (!updateCommissionFormRefs.current[hashedCommission]) {
      updateCommissionFormRefs.current[hashedCommission] = React.createRef() as FormRef;
    }
    return updateCommissionFormRefs.current[hashedCommission];
  }, []);

  const editCommission = React.useCallback(async (editedCommission: Commission) => {
    if (!isEditing) return;

    const isValid = checkCommissionLimits(
      editedCommission,
      invoice,
      addError,
      isEditing,
    );

    if (!isValid) return;

    setIsSaving(true);

    try {
      const commissions = invoice.commissions.map((commission) => {
        if (hash(commission) === hash(isEditing)) {
          return editedCommission;
        }
        return commission;
      });

      const savedInvoice = await updateInvoice(invoice.id!, {
        ...invoice,
        commissions,
      });

      setIsSaving(false);
      addSuccess(UPDATE_COMMISSION_SUCCESS_MESSAGE);
      onUpdate?.(savedInvoice);
    } catch (error) {
      setIsSaving(false);
      addError(UPDATE_COMMISSION_ERROR_MESSAGE);
    }

    setIsEditing(false);
  }, [isEditing, invoice, addError, addSuccess, onUpdate]);

  const deleteCommission = React.useCallback((commission: Commission) => async () => {
    setIsSaving(true);

    try {
      const savedInvoice = await updateInvoice(invoice.id!, {
        ...invoice,
        commissions: invoice.commissions.filter((c) => hash(c) !== hash(commission)),
      });

      setIsSaving(false);
      addSuccess(DELETE_COMMISSION_SUCCESS_MESSAGE);
      onUpdate?.(savedInvoice);
      confirmDeleteModal.close();
    } catch (error) {
      setIsSaving(false);
      addError(DELETE_COMMISSION_ERROR_MESSAGE);
    }

    setIsEditing(false);
  }, [invoice, addError, addSuccess, confirmDeleteModal, onUpdate]);

  const handleCreateCommissionSubmit: InvoiceCommissionFormProps['onSubmit'] = React.useCallback(
    async (values, actions) => {
      const shouldReset = await addCommission(commissionFormModelToCommission(values, invoice));

      if (shouldReset) {
        actions.resetForm();
      }
    },
    [addCommission, invoice],
  );

  const submitCreateCommissionForm = React.useCallback(() => {
    createCommissionFormRef.current?.handleSubmit();
  }, []);

  const submitUpdateCommissionForm = React.useCallback((hashedCommission: string) => {
    updateCommissionFormRefs.current[hashedCommission]?.current?.handleSubmit();
  }, []);

  return (
    <>
      <Stack gap={200}>
        {!!suggestedCommissions?.length && (
          <Message
            message="Suggerimenti"
            boxed
          >
            <Stack gap={200}>
              <Text.BodySmall>
                Clicca il suggerimento se presente nella lista oppure aggiungi le commissioni manualmente.
              </Text.BodySmall>

              {isMobile && (
                <Stack aria-label="Lista suggerimenti">
                  {suggestedCommissions.map((commission, i) => (
                    <InvoiceCommissionCard
                      title={`Commissione (${i + 1})`}
                      key={hash(commission)}
                      commission={commission}
                      actions={[
                        <ActionIcon
                          size="S"
                          icon={{ path: ICON_CHECK }}
                          label="Seleziona suggerimento"
                          aria-label="Pulsante per selezionare la commissione suggerita"
                          onClick={() => addCommission(commission)}
                        />,
                      ]}
                    />
                  ))}
                </Stack>
              )}

              {!isMobile && (
                <SimpleTable aria-label="Lista suggerimenti">
                  <SimpleTable.Body>
                    {suggestedCommissions.map((commission) => (
                      <SimpleTable.Row
                        key={hash(commission)}
                        asChild
                      >
                        <InvoiceCommissionTableRow
                          commission={commission}
                          actions={[
                            <ActionIcon
                              size="S"
                              icon={{ path: ICON_CHECK }}
                              label="Seleziona suggerimento"
                              aria-label="Pulsante per selezionare la commissione suggerita"
                              onClick={() => addCommission(commission)}
                            />,
                          ]}
                        />
                      </SimpleTable.Row>
                    ))}
                  </SimpleTable.Body>
                </SimpleTable>
              )}
            </Stack>
          </Message>
        )}

        {invoice.commissions.length === 0 && (
          <Message type="neutral" message="Nessuna commissione inserita." />
        )}
        {!isMobile && (
          <>
            {invoice.commissions.length > 0 && (
              <SimpleTable aria-label="Lista commissioni">
                <SimpleTable.Body>
                  {invoice.commissions.map((commission) => ((typeof isEditing === 'object' && hash(isEditing) === hash(commission)) ? (
                    <SimpleTable.Row
                      key={`invoice-commission-${hash(commission)}-edit`}
                      aria-label="Informazioni commissione (modifica)"
                    >
                      <SimpleTable.Cell colSpan={8}>
                        <div css={styles.editCommissionBox}>
                          <div css={styles.commissionForm}>
                            <InvoiceCommissionForm
                              aria-label="invoice-commission-edit-form"
                              initialValues={commissionToCommissionFormModel(commission)}
                              size="S"
                              loading={isSaving}
                              innerRef={getUpdateCommissionFormRef(hash(commission))}
                              onSubmit={(values) => editCommission(commissionFormModelToCommission(values, invoice))}
                              hideLabels
                            />
                          </div>
                        </div>
                      </SimpleTable.Cell>
                      <SimpleTable.Cell align="right">
                        <HStack>
                          <ActionIcon
                            size="S"
                            color="primary"
                            icon={{ path: ICON_CHECK }}
                            label="Conferma modifiche"
                            aria-label="Conferma modifiche"
                            onClick={() => submitUpdateCommissionForm(hash(commission))}
                            loading={isSaving}
                          />
                          <ActionIcon
                            size="S"
                            icon={{ path: ICON_CLOSE }}
                            label="Annulla modifiche"
                            aria-label="Annulla modifiche"
                            onClick={() => setIsEditing(false)}
                            loading={isSaving}
                          />
                        </HStack>
                      </SimpleTable.Cell>
                    </SimpleTable.Row>
                  ) : (
                    <SimpleTable.Row
                      asChild
                      key={`invoice-commission-${hash(commission)}`}
                      aria-label="Informazioni commissione"
                    >
                      <InvoiceCommissionTableRow
                        commission={commission}
                        actions={[
                          <ActionIcon
                            key="edit"
                            size="S"
                            icon={{ path: ICON_PENCIL_OUTLINE }}
                            label="Modifica commissione"
                            aria-label="Modifica commissione"
                            onClick={() => setIsEditing(commission)}
                          />,
                          <ActionIcon
                            key="delete"
                            size="S"
                            icon={{ path: ICON_TRASH_CAN_OUTLINE }}
                            label="Rimuovi commissione"
                            aria-label="Rimuovi commissione"
                            onClick={() => confirmDeleteModal.open(commission)}
                          />,
                        ]}
                      />
                    </SimpleTable.Row>
                  )))}
                </SimpleTable.Body>
              </SimpleTable>
            )}

            <div css={styles.addCommissionBox}>
              <div css={styles.commissionForm}>
                <InvoiceCommissionForm
                  aria-label="invoice-commission-add-form"
                  loading={isSaving}
                  innerRef={createCommissionFormRef}
                  onSubmit={handleCreateCommissionSubmit}
                  hideLabels
                  size="S"
                />
              </div>
              <Action
                size="S"
                label="Aggiungi"
                aria-label="Aggiungi commissione"
                onClick={submitCreateCommissionForm}
                loading={isSaving}
              />
            </div>
          </>
        )}

        {isMobile && (
          <Stack gap={150} aria-label="Lista commissioni">
            {invoice.commissions.map((commission, i) => ((typeof isEditing === 'object' && hash(isEditing) === hash(commission))
              ? (
                <Card key={hash(commission)} aria-label={`Informazioni commissione agente ${commission.agentId}`}>
                  <Card.Header
                    title={`Commissione (${i + 1})`}
                  />
                  <Card.Content>
                    <InvoiceCommissionForm
                      aria-label="invoice-commission-edit-form"
                      initialValues={commissionToCommissionFormModel(commission)}
                      loading={isSaving}
                      size="S"
                      innerRef={getUpdateCommissionFormRef(hash(commission))}
                      onSubmit={(values) => editCommission(commissionFormModelToCommission(values, invoice))}
                      hideLabels
                    />
                    <Divider />
                    <Grid gutter={75}>
                      <Grid.Unit size={1 / 2}>
                        <Action
                          size="S"
                          color="primary"
                          label="Salva"
                          aria-label="Conferma modifiche"
                          onClick={() => submitUpdateCommissionForm(hash(commission))}
                          width="100%"
                        />
                      </Grid.Unit>
                      <Grid.Unit size={1 / 2}>
                        <Action
                          size="S"
                          label="Annulla"
                          aria-label="Annulla modifiche"
                          onClick={() => setIsEditing(false)}
                          width="100%"
                        />
                      </Grid.Unit>
                    </Grid>
                  </Card.Content>
                </Card>
              )
              : (
                <InvoiceCommissionCard
                  key={hash(commission)}
                  title={`Commissione (${i + 1})`}
                  aria-label={`Informazioni commissione agente ${commission.agentId}`}
                  commission={commission}
                  actions={[
                    <ActionIcon
                      key="edit"
                      size="S"
                      icon={{ path: ICON_PENCIL_OUTLINE }}
                      label="Modifica commissione"
                      aria-label="Modifica commissione"
                      onClick={() => setIsEditing(commission)}
                    />,
                    <ActionIcon
                      key="delete"
                      size="S"
                      icon={{ path: ICON_TRASH_CAN_OUTLINE }}
                      label="Rimuovi commissione"
                      aria-label="Rimuovi commissione"
                      onClick={() => confirmDeleteModal.open(commission)}
                    />,
                  ]}
                />
              )))}
            <Card aria-label="Aggiungi informazioni nuova commissione">
              <Card.Header title="Aggiungi nuova commissione" />
              <Card.Content>
                <>
                  <InvoiceCommissionForm
                    aria-label="invoice-commission-add-form"
                    innerRef={createCommissionFormRef}
                    loading={isSaving}
                    onSubmit={handleCreateCommissionSubmit}
                    size="S"
                  />
                  <Divider />
                  <Action
                    label="Aggiungi"
                    size="S"
                    aria-label="Aggiungi commissione"
                    onClick={submitCreateCommissionForm}
                    width="100%"
                    loading={isSaving}
                  />
                </>
              </Card.Content>
            </Card>
          </Stack>
        )}
      </Stack>
      {confirmDeleteModal.data && (
        <Portal>
          <ConfirmModal
            isOpen={confirmDeleteModal.isOpen}
            loading={isSaving}
            title="Vuoi rimuovere la commissione?"
            aria-label="Conferma l'eliminazione della commissione"
            onConfirm={deleteCommission(confirmDeleteModal.data)}
            onAbort={confirmDeleteModal.close}
          >
            Confermando l&apos;operazione rimuoverai la commissione di importo {formatEuro(confirmDeleteModal.data.amount)} ({confirmDeleteModal.data!.percentage}%).
          </ConfirmModal>
        </Portal>
      )}
    </>
  );
};

export default ManageInvoiceCommissions;
